import { Typography } from "@mui/material";
import { useSelector } from "react-redux";

const noteTextStyle = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const toSafeText = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => toSafeText(item))
      .filter((item) => item.length > 0)
      .join("\n");
  }
  return "";
};

const hasText = (value) => toSafeText(value).length > 0;

const NoteText = ({ children }) => {
  const text = toSafeText(children);
  if (!text) return null;

  const lines = text.split("\n");
  return (
    <Typography
      className="fs12 txt-regular black"
      style={noteTextStyle}
      component="div"
    >
      {lines.map((line, index) => (
        <div key={index}>{line || "\u00A0"}</div>
      ))}
    </Typography>
  );
};

const renderSurgeryNote = (reqSoap) => {
  if (!reqSoap || typeof reqSoap !== "object") return null;
  return (
    <div className="mh20">
      <div className="fs14 blue-color font-bold mt10">
        Pre-Surgical Documentation
      </div>
      <NoteText>{reqSoap?.preSurgicalDocumentation}</NoteText>
      <div className="fs14 blue-color font-bold mt10">
        Intra-Surgical Documentation
      </div>
      <NoteText>{reqSoap?.intraSurgicalDocumentation}</NoteText>
      <div className="fs14 blue-color font-bold mt10">
        Post-Surgical Documentation
      </div>
      <NoteText>{reqSoap?.postSurgicalDocumentation}</NoteText>
      <div className="fs14 blue-color font-bold mt10">Owner Communication</div>
      <NoteText>{reqSoap?.ownerCommunication}</NoteText>
      <div className="fs14 blue-color font-bold mt10">
        Additional Documentation
      </div>
      <NoteText>{reqSoap?.additionalDocumentation}</NoteText>
    </div>
  );
};

const renderClinicalNote = (reqSoap) => {
  if (!reqSoap || typeof reqSoap !== "object") return null;
  return (
    <div className="mh20">
      {hasText(reqSoap?.tag) ? (
        <div className="fs14 blue-color font-bold mt10">
          Diagnosis: {toSafeText(reqSoap?.tag)}
        </div>
      ) : null}
      {hasText(reqSoap?.subjective) ? (
        <>
          <div className="fs14 blue-color font-bold mt10">Subjective</div>
          <NoteText>{reqSoap?.subjective}</NoteText>
        </>
      ) : null}
      {hasText(reqSoap?.objective) ? (
        <>
          <div className="fs14 blue-color font-bold mt10">Objective</div>
          <NoteText>{reqSoap?.objective}</NoteText>
        </>
      ) : null}
      {hasText(reqSoap?.assessment) ? (
        <>
          <div className="fs14 blue-color font-bold mt10">Assessment</div>
          <NoteText>{reqSoap?.assessment}</NoteText>
        </>
      ) : null}
      {hasText(reqSoap?.plan) ? (
        <>
          <div className="fs14 blue-color font-bold mt10">Plan</div>
          <NoteText>{reqSoap?.plan}</NoteText>
        </>
      ) : null}
      {hasText(reqSoap?.conclusion) ? (
        <>
          <div className="fs14 blue-color font-bold mt10">Conclusion</div>
          <NoteText>{reqSoap?.conclusion}</NoteText>
        </>
      ) : null}
    </div>
  );
};

const SoapNoteView = ({
  apntId,
  soapNote,
  soapNotes = null,
  isSurgery = false,
}) => {
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);

  const getDefaultSoapNote = () => {
    if (!apntId) return null;
    if (isSurgery) {
      const inpatientNotes = Array.isArray(reqDet?.inpatientNotes)
        ? reqDet.inpatientNotes
        : [];
      return (
        inpatientNotes.find(
          (cn) =>
            cn?.appointmentId === apntId &&
            cn?.type === "surgery" &&
            cn?.consolidated,
        ) ??
        inpatientNotes.find(
          (cn) => cn?.appointmentId === apntId && cn?.type === "surgery",
        ) ??
        null
      );
    }
    const clinicalNotes = Array.isArray(reqDet?.clinicalNotes)
      ? reqDet.clinicalNotes
      : [];
    return (
      clinicalNotes.find(
        (cn) => cn?.appointmentId === apntId && cn?.consolidated,
      ) ??
      clinicalNotes.find(
        (cn) => cn?.appointmentId === apntId && !cn?.consolidated,
      ) ??
      null
    );
  };

  const notesToRender = Array.isArray(soapNotes)
    ? soapNotes.filter((note) => note && typeof note === "object")
    : soapNote && typeof soapNote === "object"
      ? [soapNote]
      : (() => {
          const defaultNote = getDefaultSoapNote();
          return defaultNote && typeof defaultNote === "object"
            ? [defaultNote]
            : [];
        })();

  if (!notesToRender.length) return null;

  const showNoteLabels = Array.isArray(soapNotes) && notesToRender.length > 0;

  return (
    <>
      {notesToRender.map((note, index) => (
        <div key={note?.id ?? note?.clinicalNoteId ?? `soap-note-${index}`}>
          {showNoteLabels ? (
            <div
              className={`fs14 font-bold mh20${
                index > 0 ? " mt10 workbook-print-section" : ""
              }`}
            >
              Note {index + 1}
            </div>
          ) : null}
          {isSurgery ? renderSurgeryNote(note) : renderClinicalNote(note)}
        </div>
      ))}
    </>
  );
};

export default SoapNoteView;
